import React, { useEffect, useState } from "react";
import { isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import { Link, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon as CheckCircle, PawPrintIcon as PawPrint, FilePdfIcon as FilePdf, DogIcon as Dog, ArrowRightIcon as ArrowRight, StorefrontIcon as Storefront, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate, formatApiError } from "@/lib/utils-app";
import PdfModal from "@/components/PdfModal";
import DogCardDeleteButton from "@/components/DogCardDeleteButton";

/** Post-registration success page for a litter.
 *
 *  Route: /litters/:litterId/confirmation
 *
 *  Shown after a successful (free-plan OR paid) litter registration. Lists
 *  every puppy with its registration number and a prominent "Download
 *  registration PDF" button so the registrant never has to hunt through
 *  the dog list to grab their certificates.
 *
 *  The registration certificate endpoint accepts a `?auth=` query param, so
 *  a plain `<a download>` link works — no need for a JS click-to-blob step.
 */
export default function LitterConfirmation() {
  const { litterId } = useParams();
  const [litter, setLitter] = useState(null);
  const [puppies, setPuppies] = useState([]);
  const [err, setErr] = useState(null);
  const [listed, setListed] = useState(false); // true once any pup has for_sale
  const [listing, setListing] = useState(false);
  // Which puppy's registration PDF is currently open in the modal.
  // null = closed. We track the puppy object so the modal can label
  // itself with the puppy's name + register the right filename.
  const [pdfPuppy, setPdfPuppy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: lit } = await api.get(`/litters/${litterId}`);
        if (cancelled) return;
        setLitter(lit);
        // Hydrate each puppy
        const pups = await Promise.all(
          (lit.puppy_ids || []).map((id) =>
            api.get(`/dogs/${id}`).then((r) => r.data).catch(() => null)
          )
        );
        if (cancelled) return;
        const filtered = pups.filter(Boolean);
        setPuppies(filtered);
        setListed(filtered.some((p) => p.for_sale));
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Couldn't load litter.");
      }
    })();
    return () => { cancelled = true; };
  }, [litterId]);

  const listOnMarketplace = async () => {
    setListing(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "litter_marketplace_listing",
        origin_url: webOrigin(),
        litter_id: litterId,
      });
      if (data.free) {
        // Admin / promo / plan path — listing is already created.
        toast.success(
          `${data.listing.puppy_count} ${
            data.listing.puppy_count === 1 ? "puppy" : "puppies"
          } listed on the marketplace for ${data.listing.days} days.`
        );
        setListed(true);
        // Re-pull the puppies so each row reflects the new for_sale state.
        const pups = await Promise.all(
          (litter.puppy_ids || []).map((id) =>
            api.get(`/dogs/${id}`).then((r) => r.data).catch(() => null)
          )
        );
        setPuppies(pups.filter(Boolean));
      } else if (data.url) {
        // Stripe checkout — leave the page so the redirect can happen.
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setListing(false);
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const regCertUrl = (id) =>
    `${apiBase}/api/dogs/${id}/registration-certificate.pdf?auth=${encodeURIComponent(token)}`;

  if (err) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto text-sm text-crimson">{err}</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="litter-confirmation">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle size={26} weight="duotone" className="text-gold" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
            Litter registered
          </p>
        </div>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight mb-3">
          {litter ? (
            <>
              {litter.sire_name} <span className="text-crimson">×</span> {litter.dam_name}
            </>
          ) : (
            "Registering…"
          )}
        </h1>
        {litter && (
          <p className="text-[#6B6B6B] max-w-2xl">
            Whelped {formatDate(litter.whelp_date)} ·{" "}
            <strong>{(litter.puppy_ids || []).length}</strong>{" "}
            {(litter.puppy_ids || []).length === 1 ? "puppy" : "puppies"} registered.
            Each puppy has its own Certificate of Registration — download them below. Copies were
            also emailed to you automatically.
          </p>
        )}

        {litter && (litter.puppy_ids || []).length > 0 && (
          <section
            className={`mt-8 rounded-2xl border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 ${
              listed
                ? "bg-[#F0F8F1] border-[#0F8043]/30"
                : "bg-gradient-to-br from-[#FBF7EC] via-white to-[#FFF7E6] border-[#C9A227]/40"
            }`}
            data-testid="litter-marketplace-cta"
          >
            <div className="h-12 w-12 rounded-xl bg-white border border-[#C9A227]/40 flex items-center justify-center flex-shrink-0 shadow-sm">
              {listed ? (
                <CheckCircle size={24} weight="duotone" className="text-[#0F8043]" />
              ) : (
                <Storefront size={22} weight="duotone" className="text-gold" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-spectral text-xl">
                  {listed ? "Listed on the marketplace" : "List this litter on the marketplace"}
                </h2>
                {!listed && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold bg-[#0E0E0E] text-gold rounded-full px-2 py-0.5">
                    <Sparkle size={10} weight="duotone" /> Add-on · $19.99
                  </span>
                )}
              </div>
              <p className="text-sm text-[#3F3F3F] mt-1 leading-relaxed">
                {listed ? (
                  <>
                    All {(litter.puppy_ids || []).length}{" "}
                    {(litter.puppy_ids || []).length === 1 ? "puppy is" : "puppies are"}{" "}
                    on the marketplace for 60 days. Set asking prices on each
                    puppy's profile any time — buyers see their listing as
                    "price on request" until you do.
                  </>
                ) : (
                  <>
                    One click puts every puppy in this litter in front of
                    registered buyers for <strong>60 days</strong>. They'll show
                    up in marketplace search with breed filters, your kennel
                    name, and a "price on request" tag — set prices later on
                    each puppy's profile.
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {listed ? (
                <Link to="/marketplace">
                  <Button
                    variant="outline"
                    className="rounded-full border-[#0F8043]/40 text-[#0F8043] hover:bg-[#0F8043]/5"
                    data-testid="view-marketplace-btn"
                  >
                    View marketplace <ArrowRight size={13} weight="bold" className="ml-1.5" />
                  </Button>
                </Link>
              ) : isIOS() ? (
                <IosPurchaseNotice label="Marketplace listing" />
              ) : (
                <Button
                  onClick={listOnMarketplace}
                  disabled={listing}
                  className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-5"
                  data-testid="list-litter-btn"
                >
                  <Storefront size={14} weight="duotone" className="mr-1.5" />
                  {listing ? "Processing…" : "List for $19.99"}
                </Button>
              )}
            </div>
          </section>
        )}

        <div className="mt-8 space-y-3" data-testid="puppy-cert-list">
          {puppies.map((p) => (
            <div
              key={p.id}
              className="bg-white border border-[#E5E5E5] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-[0_8px_30px_rgba(0,0,0,0.03)]"
              data-testid={`puppy-cert-row-${p.id}`}
            >
              <div className="flex items-start gap-4 min-w-0">
                <div className="h-11 w-11 rounded-full bg-[#FBF7EC] border border-[#E8D9A6] flex items-center justify-center flex-shrink-0">
                  <PawPrint size={20} weight="duotone" className="text-gold" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-spectral text-xl tracking-tight truncate">{p.name}</h3>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#0E0E0E] text-gold rounded-full px-2 py-0.5">
                      {(p.sex || "").toUpperCase() || "—"}
                    </span>
                  </div>
                  <div className="text-sm text-[#6B6B6B] mt-0.5">
                    {p.breed || "—"} {p.color ? `· ${p.color}` : ""}
                  </div>
                  <div className="text-xs font-mono text-[#3F3F3F] mt-1">
                    Reg # <strong>{p.registration_number || "—"}</strong>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={`/dogs/${p.id}`}>
                  <Button
                    variant="outline"
                    className="rounded-full border-[#E5E5E5]"
                    data-testid={`puppy-view-btn-${p.id}`}
                  >
                    <Dog size={14} className="mr-1.5" /> View profile
                  </Button>
                </Link>
                <button
                  type="button"
                  onClick={() => setPdfPuppy(p)}
                  data-testid={`puppy-download-cert-${p.id}`}
                >
                  <Button asChild={false} className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white pointer-events-none">
                    <span className="inline-flex items-center justify-center">
                      <FilePdf size={14} className="mr-1.5" weight="duotone" /> Registration PDF
                    </span>
                  </Button>
                </button>
                {/* Inline delete for the most common moment a breeder
                    spots a duplicate puppy — right after registering
                    the litter, before they download certificates. */}
                <DogCardDeleteButton
                  dogId={p.id}
                  dogName={p.name}
                  registrationNumber={p.registration_number}
                  size="compact"
                  onDeleted={() =>
                    setPuppies((prev) => prev.filter((x) => x.id !== p.id))
                  }
                />
              </div>
            </div>
          ))}
          {litter && (litter.puppy_ids || []).length === 0 && (
            <p className="text-sm text-[#6B6B6B]">
              No puppies were registered for this litter.
            </p>
          )}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 justify-between border-t border-[#E5E5E5] pt-6">
          <Link to="/litters" className="text-sm text-[#3F3F3F] hover:text-ink inline-flex items-center gap-1">
            All litters <ArrowRight size={13} weight="bold" />
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" className="rounded-full border-[#E5E5E5]">
              Back to dashboard
            </Button>
          </Link>
        </div>
      </div>
      <PdfModal
        open={pdfPuppy !== null}
        onClose={() => setPdfPuppy(null)}
        src={pdfPuppy ? regCertUrl(pdfPuppy.id) : ""}
        title={pdfPuppy ? `Registration certificate · ${pdfPuppy.name || "Puppy"}` : ""}
        downloadName={
          pdfPuppy
            ? `registration-${(pdfPuppy.name || pdfPuppy.id).replace(/\s+/g, "-").toLowerCase()}.pdf`
            : "registration.pdf"
        }
      />
    </AppShell>
  );
}
